import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useLanguage, LANGUAGES } from '../context/LanguageContext'
import './premium-navbar.css'

const ChevronIcon = () => (
  <svg className="nav-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
)

const MenuIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
)

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
)

function LanguageSwitcher() {
  const { lang, setLang, langMeta } = useLanguage()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        id="language-switcher-btn"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          background: 'rgba(197,160,89,0.10)',
          border: '1px solid rgba(197,160,89,0.35)',
          borderRadius: '8px',
          padding: '7px 12px',
          cursor: 'pointer',
          fontFamily: "'Poppins', sans-serif",
          fontSize: '13px',
          fontWeight: 500,
          color: '#002E53',
          transition: 'all 0.2s',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(197,160,89,0.20)'; e.currentTarget.style.borderColor = 'rgba(197,160,89,0.6)' }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(197,160,89,0.10)'; e.currentTarget.style.borderColor = 'rgba(197,160,89,0.35)' }}
        aria-label="Select language"
        aria-expanded={open}
      >
        <span style={{ fontSize: '16px' }}>{langMeta.flag}</span>
        <span>{langMeta.label}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          [lang === 'ar' ? 'left' : 'right']: 0,
          background: '#fff',
          border: '1px solid rgba(0,46,83,0.1)',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,46,83,0.12)',
          minWidth: '180px',
          overflow: 'hidden',
          zIndex: 9999,
          animation: 'fadeInDown 0.18s ease',
        }}>
          {LANGUAGES.map(l => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); setOpen(false) }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 16px',
                background: l.code === lang ? 'rgba(197,160,89,0.1)' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontFamily: l.font === 'Noto Sans Arabic' ? "'Noto Sans Arabic', sans-serif"
                  : l.font === 'Noto Sans Devanagari' ? "'Noto Sans Devanagari', sans-serif"
                  : l.font === 'Noto Sans SC' ? "'Noto Sans SC', sans-serif"
                  : "'Poppins', sans-serif",
                fontSize: '14px',
                fontWeight: l.code === lang ? 600 : 400,
                color: l.code === lang ? '#C5A059' : '#002E53',
                textAlign: 'left',
                transition: 'background 0.15s',
                borderLeft: l.code === lang ? '3px solid #C5A059' : '3px solid transparent',
              }}
              onMouseEnter={e => { if (l.code !== lang) e.currentTarget.style.background = 'rgba(0,46,83,0.04)' }}
              onMouseLeave={e => { if (l.code !== lang) e.currentTarget.style.background = 'transparent' }}
            >
              <span style={{ fontSize: '18px' }}>{l.flag}</span>
              <span>{l.label}</span>
              {l.code === lang && <span style={{ marginLeft: 'auto', color: '#C5A059', fontSize: '12px' }}>✓</span>}
            </button>
          ))}
        </div>
      )}

      <style>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}

function Navbar() {
  const { t } = useLanguage()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [mobileExpanded, setMobileExpanded] = useState(new Set())
  const navigate = useNavigate()
  const location = useLocation()

  const toggleMobileExpand = (index) => {
    setMobileExpanded(prev => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  const NAV_ITEMS = [
    { label: t('nav.home'), path: '/' },
    { label: t('nav.properties'), items: [
        { label: t('nav.buy'),  path: '/properties/buy' },
        { label: t('nav.rent'), path: '/properties/rent' },
      ]
    },
    { label: t('nav.sellRent'), path: '/sell-rent' },
    { label: t('nav.offplan'), path: '/offplan' },
    { label: t('nav.services'), items: [
        { label: t('nav.holidayHomes'), path: '/services/holiday-homes' },
        { label: t('nav.propertyManagement'), path: '/services/property-management' },
        { label: t('nav.fitOut'), path: '/services/fit-out' },
        { label: t('nav.legalAssistance'), path: '/services/legal-assistance' },
        { label: t('nav.valuation'), path: '/valuation' },
      ]
    },
    { label: t('nav.calculators'), items: [
        { label: t('nav.areaIntelligence'), path: '/area-intelligence' },
        { label: t('nav.mortgage'), path: '/mortgage' },
        { label: t('nav.roiCalc'), path: '/roi-calculator' },
      ]
    },
    { label: t('nav.transactions'), path: '#transactions', isScroll: true },
    { label: t('nav.contact'), path: '/contact' },
  ]

  function isActive(item) {
    if (item.items) return item.items.some(sub => location.pathname.startsWith(sub.path))
    if (item.path === '/') return location.pathname === '/'
    return location.pathname.startsWith(item.path)
  }

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : 'unset'
    return () => { document.body.style.overflow = 'unset' }
  }, [mobileOpen])

  const handleScrollToSection = (id, e) => {
    e.preventDefault()
    setMobileOpen(false)
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    } else {
      navigate('/')
      setTimeout(() => {
        const target = document.getElementById(id)
        if (target) target.scrollIntoView({ behavior: 'smooth' })
      }, 300)
    }
  }

  return (
    <>
      <header className={`premium-navbar${scrolled ? ' scrolled' : ''}`}>
        <Link to="/" className="premium-logo">
          <img src="/assets/Victory Homes logo design.png" alt="Victory Homes Logo" />
        </Link>

        <nav className="premium-desktop-nav">
          {NAV_ITEMS.map((item, i) => (
            <div key={i} className="nav-item">
              {item.isScroll ? (
                <a href={item.path} className={`nav-link${isActive(item) ? ' nav-link--active' : ''}`} onClick={(e) => handleScrollToSection(item.path.replace('#', ''), e)}>
                  {item.label}
                  {item.items && <ChevronIcon />}
                </a>
              ) : item.items ? (
                <span className={`nav-link${isActive(item) ? ' nav-link--active' : ''}`} style={{ cursor: 'default' }}>
                  {item.label}
                  <ChevronIcon />
                </span>
              ) : (
                <Link to={item.path} className={`nav-link${isActive(item) ? ' nav-link--active' : ''}`}>
                  {item.label}
                </Link>
              )}

              {item.items && (
                <div className="premium-dropdown">
                  {item.items.map((sub, j) =>
                    sub.isGroupHeader ? (
                      <div key={j} style={{
                        padding: '8px 16px 4px', fontSize: '10px', fontWeight: 700,
                        letterSpacing: '1.2px', textTransform: 'uppercase',
                        color: '#C5A059', borderTop: j > 0 ? '1px solid rgba(0,46,83,0.07)' : 'none',
                        marginTop: j > 0 ? '4px' : '0',
                      }}>
                        {sub.label}
                      </div>
                    ) : (
                      <Link key={j} to={sub.path} className="dropdown-link">{sub.label}</Link>
                    )
                  )}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <LanguageSwitcher />
          <button className="mobile-toggle" onClick={() => setMobileOpen(true)} aria-label="Open Menu">
            <MenuIcon />
          </button>
        </div>
      </header>

      <div className={`mobile-overlay ${mobileOpen ? 'open' : ''}`} onClick={() => setMobileOpen(false)}></div>

      <div className={`premium-mobile-drawer ${mobileOpen ? 'open' : ''}`}>
        <div className="mobile-drawer-header">
          <button className="close-drawer" onClick={() => setMobileOpen(false)} aria-label="Close Menu">
            <CloseIcon />
          </button>
        </div>

        {/* Language switcher inside mobile drawer */}
        <div style={{ padding: '0 20px 16px' }}>
          <LanguageSwitcher />
        </div>

        <div className="mobile-nav-items">
          {NAV_ITEMS.map((item, i) => (
            <div key={i} className="mobile-nav-item">
              {item.isScroll ? (
                <a href={item.path} className="mobile-nav-link" onClick={(e) => handleScrollToSection(item.path.replace('#', ''), e)}>
                  {item.label}
                </a>
              ) : item.items ? (
                <button
                  className="mobile-nav-link mobile-nav-link--expandable"
                  onClick={() => toggleMobileExpand(i)}
                  aria-expanded={mobileExpanded.has(i)}
                >
                  {item.label}
                  <svg
                    width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round"
                    style={{
                      marginLeft: 'auto',
                      transform: mobileExpanded.has(i) ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.25s ease',
                      flexShrink: 0,
                    }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              ) : (
                <Link to={item.path} className="mobile-nav-link" onClick={() => setMobileOpen(false)}>
                  {item.label}
                </Link>
              )}

              {item.items && mobileExpanded.has(i) && (
                <div className="mobile-dropdown-container">
                  {item.items.map((sub, j) =>
                    sub.isGroupHeader ? (
                      <div key={j} style={{
                        padding: '5px 12px 3px', fontSize: '10px', fontWeight: 700,
                        letterSpacing: '1.2px', textTransform: 'uppercase',
                        color: '#C5A059',
                        borderTop: j > 0 ? '1px solid rgba(0,46,83,0.07)' : 'none',
                        marginTop: j > 0 ? '4px' : '0',
                      }}>
                        {sub.label}
                      </div>
                    ) : (
                      <Link key={j} to={sub.path} className="mobile-sub-link" onClick={() => setMobileOpen(false)}>
                        {sub.label}
                      </Link>
                    )
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

export default Navbar
